import { FunctionComponent, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import styled from 'styled-components';
import { DashboardWrapper, Card, Text } from 'src/shared';
import { Colors } from 'src/utils/color';
import { Size } from 'src/shared/text';

import { ReactComponent as UploadIcon } from 'src/icons/upload.svg';
import { ReactComponent as UserCircleIcon } from 'src/icons/user-circle.svg';
import { ReactComponent as LoadingIcon } from 'src/icons/loading-1.svg';
import { ReactComponent as ScanIcon } from 'src/icons/scan.svg';
import { ReactComponent as ArrowIcon } from 'src/icons/arrow-forward-simple.svg';

// ---------------------------------------------------------------------------
// Data
// ---------------------------------------------------------------------------
const STEPS = [
    {
        icon: UploadIcon,
        title: 'Upload a Scan',
        body: 'Select one or more .hdf5 files to upload.',
    },
    {
        icon: UserCircleIcon,
        title: 'Confirm Patient Info',
        body: 'Patient ID is auto-extracted from the file. Edit it if needed.',
    },
    {
        icon: LoadingIcon,
        title: 'Processing',
        body: 'Analysis starts after upload. Track progress on the Scan Analysis page.',
    },
    {
        icon: ScanIcon,
        title: 'Review Results',
        body: 'Fixation metrics, microsaccade analysis, annotated videos, and downloadable CSV/PDF reports.',
    },
];

const FAQ_ITEMS: { q: string; a: string }[] = [
    { q: 'What file types are supported?', a: 'HDF5 (.h5, .hdf5) files from CLight imaging devices.' },
    { q: 'What is the max file size?', a: '4 GB per individual file.' },
    { q: 'How long does analysis take?', a: 'Processing time varies depending on file size and the number of scans within the recording.' },
    { q: 'What happens if processing fails?', a: 'The failure reason appears on the Scan Analysis page. You can retry the analysis from there.' },
    { q: 'Do I need to stay on the page during upload?', a: 'No, but keep the browser tab open until the upload completes.' },
    { q: 'Who can see my data?', a: 'Access is limited to authorized members of your clinical group. Data is stored securely in AWS with encryption at rest.' },
    { q: 'What outputs do I get?', a: 'Fixation metrics, microsaccade analysis, annotated videos, and downloadable CSV/PDF reports.' },
];

const TIPS = [
    'Use consistent patient IDs across uploads for clean longitudinal tracking.',
    'You can upload multiple files at once from the Upload page.',
    'Results are automatically available to your clinical group \u2014 no need to share manually.',
];

// ---------------------------------------------------------------------------
// Styled components
// ---------------------------------------------------------------------------
const Container = styled.div`
    display: flex;
    flex-direction: column;
    gap: 24px;
    padding: 24px;
    max-width: 900px;
`;

const CTAButton = styled.button`
    width: 100%;
    padding: 14px 0;
    border: none;
    border-radius: 10px;
    background: ${Colors.theme.primary};
    color: #fff;
    font-size: 15px;
    font-weight: 600;
    cursor: pointer;
    transition: opacity 0.15s ease;
    &:hover { opacity: 0.88; }
`;

const SectionDivider = styled.hr`
    border: none;
    border-top: 1px solid ${Colors.extra.light_gray};
    margin: 8px 0;
`;

// Steps grid
const StepsGrid = styled.div`
    display: grid;
    grid-template-columns: repeat(4, 1fr);
    gap: 20px;
    @media (max-width: 768px) {
        grid-template-columns: repeat(2, 1fr);
    }
`;

const StepCard = styled(Card)`
    padding: 20px 16px;
    display: flex;
    flex-direction: column;
    align-items: center;
    text-align: center;
    gap: 8px;
    position: relative;
    box-shadow: 0 2px 8px rgba(0, 0, 0, 0.08);
    transition: box-shadow 0.2s ease, transform 0.2s ease;
    &:hover {
        box-shadow: 0 4px 14px rgba(0, 0, 0, 0.12);
        transform: translateY(-2px);
    }
`;

const StepIconWrap = styled.div`
    width: 36px;
    height: 36px;
    display: flex;
    align-items: center;
    justify-content: center;
    color: ${Colors.theme.primary};
    svg { width: 28px; height: 28px; fill: currentColor; }
`;

const StepNumber = styled.div`
    width: 32px;
    height: 32px;
    border-radius: 50%;
    background: ${Colors.theme.primary};
    color: #fff;
    font-size: 14px;
    font-weight: 700;
    display: flex;
    align-items: center;
    justify-content: center;
    flex-shrink: 0;
`;

const StepTitle = styled.div`
    font-size: 14px;
    font-weight: 600;
    color: ${Colors.extra.black};
`;

const StepBody = styled.div`
    font-size: 12px;
    color: ${Colors.extra.dark_gray};
    line-height: 1.5;
`;

const StepArrow = styled.div`
    position: absolute;
    right: 0;
    top: 50%;
    transform: translate(50%, -50%);
    color: #6B7280;
    svg { width: 20px; height: 20px; fill: currentColor; }
    @media (max-width: 768px) { display: none; }
`;

// FAQ
const SectionCard = styled(Card)`
    padding: 20px;
`;

const SectionTitle = styled.div`
    font-size: 14px;
    font-weight: 600;
    color: ${Colors.extra.black};
    margin-bottom: 12px;
`;

const FAQItem = styled.div`
    border-bottom: 1px solid ${Colors.extra.light_gray};
    &:last-child { border-bottom: none; }
`;

const FAQQuestion = styled.button`
    width: 100%;
    display: flex;
    justify-content: space-between;
    align-items: center;
    padding: 10px 0;
    border: none;
    background: none;
    cursor: pointer;
    font-size: 13px;
    font-weight: 500;
    color: ${Colors.extra.black};
    text-align: left;
`;

const FAQToggle = styled.span<{ $open: boolean }>`
    font-size: 16px;
    color: ${Colors.extra.dark_gray};
    transition: transform 0.15s ease;
    transform: rotate(${p => (p.$open ? 180 : 0)}deg);
    flex-shrink: 0;
    margin-left: 12px;
`;

const FAQAnswer = styled.div`
    font-size: 12px;
    color: ${Colors.extra.dark_gray};
    line-height: 1.6;
    padding: 0 0 10px 0;
`;

// Tips
const TipsCard = styled.div`
    padding: 20px;
    border-radius: 8px;
    background: rgba(37, 125, 230, 0.04);
    border: 1px solid rgba(37, 125, 230, 0.15);
`;

const TipsTitle = styled.div`
    font-size: 14px;
    font-weight: 600;
    color: ${Colors.extra.black};
    margin-bottom: 12px;
`;

const TipsList = styled.ul`
    margin: 0;
    padding: 0 0 0 18px;
    display: flex;
    flex-direction: column;
    gap: 8px;
`;

const TipItem = styled.li`
    font-size: 13px;
    color: ${Colors.extra.dark_gray};
    line-height: 1.5;
`;

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------
const GetStartedPage: FunctionComponent = () => {
    const navigate = useNavigate();
    const [openFaq, setOpenFaq] = useState<number | null>(null);

    return (
        <DashboardWrapper>
            <Container>
                <div>
                    <Text fontSize={Size.Large} fontWeight="600" color={Colors.extra.black}>
                        Get Started
                    </Text>
                    <Text fontSize={Size.Small} color={Colors.extra.dark_gray} marginTop={8}>
                        Upload scans, track processing, and review results
                    </Text>
                </div>

                {/* Hero CTA */}
                <CTAButton onClick={() => navigate('/retina/upload')}>
                    Upload Your First Scan to Start Analysis
                </CTAButton>

                {/* Step flow */}
                <StepsGrid>
                    {STEPS.map((step, i) => {
                        const Icon = step.icon;
                        return (
                            <StepCard key={i}>
                                <StepIconWrap><Icon /></StepIconWrap>
                                <StepNumber>{i + 1}</StepNumber>
                                <StepTitle>{step.title}</StepTitle>
                                <StepBody>{step.body}</StepBody>
                                {i < STEPS.length - 1 && (
                                    <StepArrow><ArrowIcon /></StepArrow>
                                )}
                            </StepCard>
                        );
                    })}
                </StepsGrid>

                {/* CTA repeat */}
                <CTAButton onClick={() => navigate('/retina/upload')}>
                    Upload Your First Scan to Start Analysis
                </CTAButton>

                <SectionDivider />

                {/* FAQ */}
                <SectionCard>
                    <SectionTitle>Frequently Asked Questions</SectionTitle>
                    {FAQ_ITEMS.map((item, i) => (
                        <FAQItem key={i}>
                            <FAQQuestion onClick={() => setOpenFaq(openFaq === i ? null : i)}>
                                {item.q}
                                <FAQToggle $open={openFaq === i}>&#9662;</FAQToggle>
                            </FAQQuestion>
                            {openFaq === i && <FAQAnswer>{item.a}</FAQAnswer>}
                        </FAQItem>
                    ))}
                </SectionCard>

                <SectionDivider />

                {/* Tips */}
                <TipsCard>
                    <TipsTitle>Tips</TipsTitle>
                    <TipsList>
                        {TIPS.map((tip, i) => (
                            <TipItem key={i}>{tip}</TipItem>
                        ))}
                    </TipsList>
                </TipsCard>
            </Container>
        </DashboardWrapper>
    );
};

export default GetStartedPage;
